// Optimized Contact Form Component - Performance Enhanced with reCAPTCHA v3 and i18n support
import React, { useState, useCallback, useMemo, useEffect } from "react"
import { useTranslation } from "gatsby-plugin-react-i18next"

const ContactForm = ({ accountId, agencyId, familyName, familyType }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contactNumber: '',
    state: '',
    pregnant: '',
    message: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success', 'error', null
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)

  const isClient = typeof window !== 'undefined'

  // Translation helpers for family type
  const familyTypeText = familyType === "Couple" ? t('common.us') : t('common.me')
  const familyTypeTextCapitalized = familyType === "Couple" ? t('common.usCapitalized') : t('common.meCapitalized')
  const recaptchaSiteKey = process.env.GATSBY_RECAPTCHA_SITE_KEY || process.env.RECAPTCHA_SITE_KEY

  // Compact reCAPTCHA loader
  const loadRecaptchaScript = useCallback(async () => {
    if (!recaptchaSiteKey || !isClient) return false
    
    if (window.grecaptcha?.ready) {
      return new Promise(resolve => window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true)
        resolve(true)
      }))
    }

    const existing = document.querySelector(`script[src*="recaptcha/api.js"]`)
    if (existing) {
      return new Promise(resolve => {
        const check = () => window.grecaptcha?.ready ? 
          window.grecaptcha.ready(() => { setRecaptchaLoaded(true); resolve(true) }) : 
          setTimeout(() => resolve(!!window.grecaptcha), 500)
        
        existing.readyState === 'complete' ? check() : 
        existing.addEventListener('load', check, { once: true })
      })
    }

    return new Promise(resolve => {
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`
      script.async = script.defer = true
      script.onload = () => window.grecaptcha?.ready ? 
        window.grecaptcha.ready(() => { setRecaptchaLoaded(true); resolve(true) }) : resolve(false)
      script.onerror = () => resolve(false)
      document.head.appendChild(script)
    })
  }, [recaptchaSiteKey])

  // Compact reCAPTCHA executor
  const executeRecaptcha = useCallback(async (action = 'contact_form') => {
    try {
      if (!recaptchaLoaded && !(await loadRecaptchaScript())) return null
      return window.grecaptcha?.execute(recaptchaSiteKey, { action }) || null
    } catch { return null }
  }, [recaptchaLoaded, recaptchaSiteKey, loadRecaptchaScript])

  // Memoize US States list to prevent recreation on each render
  const states = useMemo(() => [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
    'Wisconsin', 'Wyoming'
  ], [])

  // Memoized phone number formatting function
  const formatPhoneNumber = useCallback((value) => {
    if (!value) return value
    const phoneNumber = value.replace(/[^\d]/g, '')
    const phoneNumberLength = phoneNumber.length
    if (phoneNumberLength < 4) return phoneNumber
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
  }, [])

  // Pregnancy status options with translation keys
  const pregnancyOptions = useMemo(() => [
    t('contact.pregnancyOptions.pregnant'),
    t('contact.pregnancyOptions.justGaveBirth')
  ], [t])

  // Memoized form validation function
  const validateForm = useCallback(() => {
    const errors = {}
    
    if (!formData.name.trim()) {
      errors.name = t('financialModal.validation.nameRequired')
    }
    
    if (!formData.email.trim()) {
      errors.email = t('financialModal.validation.emailRequired')
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('financialModal.validation.emailInvalid')
    }
    
    if (!formData.contactNumber.trim()) {
      errors.contactNumber = t('financialModal.validation.phoneRequired')
    } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.contactNumber)) {
      errors.contactNumber = t('financialModal.validation.phoneInvalid')
    }
    
    if (!formData.state) {
      errors.state = t('contact.validation.stateRequired')
    }
    
    if (!formData.pregnant) {
      errors.pregnant = t('contact.validation.pregnancyRequired')
    }
    
    return errors
  }, [formData, t])

  // Optimized API submission function
  const submitToAPI = useCallback(async (payload) => {
    const apiUrl = process.env.GATSBY_PF_API_URL || process.env.PF_API_URL
    
    const response = await fetch(`${apiUrl}DomainForm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        account: accountId,
        agency: agencyId,
        family: familyName,
        domain: typeof window !== 'undefined' ? window.location.href : ''
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    return await response.json()
  }, [accountId, agencyId, familyName])

  // Optimized input change handler
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target
    
    setFormData(prev => {
      const newData = { ...prev }
      
      // Format phone number as user types
      if (name === 'contactNumber') {
        newData[name] = formatPhoneNumber(value)
      } else {
        newData[name] = value
      }
      
      return newData
    })
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
    
    // Clear submit status when user makes changes
    if (submitStatus) {
      setSubmitStatus(null)
    }
  }, [formErrors, formatPhoneNumber, submitStatus])

  // Scroll to top of form
  const scrollToTop = useCallback(() => {
    if (!isClient) return
    // Scroll to the form section
    const formSection = document.querySelector('#contact-heading')
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  // Optimized form submission handler with reCAPTCHA
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    const errors = validateForm()
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      setSubmitStatus('error')
      // Scroll to top to show error messages
      setTimeout(scrollToTop, 100)
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)
    
    try {
      let recaptchaToken = null
      
      // Get reCAPTCHA token
      if (recaptchaSiteKey) {
        recaptchaToken = await executeRecaptcha('contact_form_submit')
      }

      const payload = {
        type: 'Contact',
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.contactNumber,
        state: formData.state,
        pregnancy_status: formData.pregnant,
        message: formData.message.trim(),
        ...(recaptchaToken && { recaptcha_token: recaptchaToken }) // Include token if available
      }

      await submitToAPI(payload)
      
      setSubmitStatus('success')
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        contactNumber: '',
        state: '',
        pregnant: '',
        message: ''
      })
      setFormErrors({})
      
      // Scroll to top to show success message
      setTimeout(scrollToTop, 100)
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000)
      
    } catch (error) {
      console.error('Error submitting contact form:', error)
      setSubmitStatus('error')
      
      // Check if error is reCAPTCHA related
      if (error.message.includes('reCAPTCHA')) {
        setFormErrors({ 
          submit: t('financialModal.securityError')
        })
      } else {
        setFormErrors({ 
          submit: t('financialModal.errorMessage')
        })
      }
      
      // Scroll to top to show error message
      setTimeout(scrollToTop, 100)
    } finally {
      setIsSubmitting(false)
    }
  }, [formData, validateForm, submitToAPI, executeRecaptcha, recaptchaLoaded, recaptchaSiteKey, scrollToTop, t])

  // Memoized error/success messages
  const submitMessage = useMemo(() => {
    if (submitStatus === 'success') {
      return (
        <div className="mb-8 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl" role="alert">
          <div className="flex items-center">
            <span className="mr-2">✅</span>
            <span className="font-semibold font-heading-sub">{t('contact.successMessage')}</span>
          </div>
          <p className="mt-1 text-sm font-body">{t('contact.submitSuccess')}</p>
        </div>
      )
    }
    
    if (submitStatus === 'error' && formErrors.submit) {
      return (
        <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-xl" role="alert">
          <div className="flex items-center">
            <span className="mr-2">⚠️</span>
            <span className="font-semibold font-heading-sub">{t('common.error')}</span>
          </div>
          <p className="mt-1 text-sm font-body">{formErrors.submit}</p>
        </div>
      )
    }
    
    return null
  }, [submitStatus, formErrors.submit, t])

  // Show warning if reCAPTCHA is not configured
  const recaptchaWarning = useMemo(() => {
    if (!recaptchaSiteKey) {
      return (
        <div className="mb-8 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-xl" role="alert">
          <div className="flex items-center">
            <span className="mr-2">⚠️</span>
            <span className="font-semibold font-heading-sub">{t('contact.warning')}</span>
          </div>
          <p className="mt-1 text-sm font-body">{t('financialModal.recaptchaWarning')}</p>
        </div>
      )
    }
    return null
  }, [recaptchaSiteKey, t])

  // Input field styling classes
  const getInputClassName = useCallback((fieldName) => {
    return `w-full px-6 py-4 rounded-2xl border focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-lg transition-colors duration-200 font-body ${
      formErrors[fieldName] 
        ? 'border-red-500 bg-red-50' 
        : isSubmitting 
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
          : 'border-gray-300 hover:border-gray-400'
    }`
  }, [formErrors, isSubmitting])

  return (
    <section className="py-20 bg-white" aria-labelledby="contact-heading">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 
            id="contact-heading"
            className="text-4xl lg:text-5xl text-gray-600 font-heading-main" 
            dangerouslySetInnerHTML={{
              __html: t('contact.sendMessage', { 
                familyTypeText: familyTypeTextCapitalized,
                messageSpan: `<span class="text-accent">${t('contact.messageWord')}</span>`
              })
            }}
          />
        </div>

        {/* reCAPTCHA Warning */}
        {recaptchaWarning}

        {/* Submit Status Messages */}
        {submitMessage}

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-8" noValidate>
          
          {/* Name and Contact Number Row */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label 
                className="block font-semibold text-gray-800 mb-3 text-lg font-heading-sub" 
                htmlFor="name"
              >
                {t('financialModal.fields.name')} *
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                autoComplete="off"
                placeholder={t('contact.enterNamePlaceholder')}
                disabled={isSubmitting}
                className={getInputClassName('name')}
                aria-describedby={formErrors.name ? 'name-error' : undefined}
              />
              {formErrors.name && (
                <p 
                  id="name-error"
                  className="text-red-600 text-base font-semibold mt-2 font-heading-sub" 
                  role="alert"
                >
                  ⚠️ {formErrors.name}
                </p>
              )}
            </div>
            
            <div>
              <label 
                className="block font-semibold text-gray-800 mb-3 text-lg font-heading-sub" 
                htmlFor="contactNumber"
              >
                {t('financialModal.fields.phone')} *
              </label>
              <input
                id="contactNumber"
                autoComplete="off"
                type="tel"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleInputChange}
                required
                placeholder={t('financialModal.placeholders.phone')}
                disabled={isSubmitting}
                className={getInputClassName('contactNumber')}
                aria-describedby={formErrors.contactNumber ? 'contactNumber-error' : undefined}
              />
              {formErrors.contactNumber && (
                <p 
                  id="contactNumber-error"
                  className="text-red-600 text-base font-semibold mt-2 font-heading-sub" 
                  role="alert"
                >
                  ⚠️ {formErrors.contactNumber}
                </p>
              )}
            </div>
          </div>

          {/* Email and State Row */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label 
                className="block font-semibold text-gray-800 mb-3 text-lg font-heading-sub" 
                htmlFor="email"
              >
                {t('financialModal.fields.email')} *
              </label>
              <input
                id="email"
                autoComplete="off"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder={t('contact.enterEmailPlaceholder')}
                disabled={isSubmitting}
                className={getInputClassName('email')}
                aria-describedby={formErrors.email ? 'email-error' : undefined}
              />
              {formErrors.email && (
                <p 
                  id="email-error"
                  className="text-red-600 text-base font-semibold mt-2 font-heading-sub" 
                  role="alert"
                >
                  ⚠️ {formErrors.email}
                </p>
              )}
            </div>
            
            <div>
              <label 
                className="block font-semibold text-gray-800 mb-3 text-lg font-heading-sub" 
                htmlFor="state"
              >
                {t('contact.stateLabel')} *
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className={`${getInputClassName('state')} appearance-none bg-white`}
                aria-label={t('contact.stateLabel')}
                aria-required="true"
                aria-describedby={formErrors.state ? 'state-error' : undefined}
              >
                <option value="">{t('contact.selectStatePlaceholder')}</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {formErrors.state && (
                <p 
                  id="state-error"
                  className="text-red-600 text-base font-semibold mt-2 font-heading-sub" 
                  role="alert"
                >
                  ⚠️ {formErrors.state}
                </p>
              )}
            </div>
          </div>

          {/* Pregnancy Status Radio Buttons */}
          <fieldset>
            <legend className="block font-semibold text-gray-800 mb-6 text-lg font-heading-sub">
              {t('contact.pregnancyStatusLabel')} *
            </legend>
            <div className="space-y-4">
              {pregnancyOptions.map((option, index) => (
                <label key={index} className="flex items-start cursor-pointer group">
                  <input
                    type="radio"
                    name="pregnant"
                    autoComplete="off"
                    value={option}
                    checked={formData.pregnant === option}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="w-5 h-5 text-gray-600 border-2 border-gray-300 focus:ring-2 focus:ring-accent focus:ring-offset-0 mt-1 transition-colors duration-200"
                  />
                  <span 
                    className={`ml-3 text-lg transition-colors duration-200 font-body ${
                      isSubmitting ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-900'
                    }`} 
                  >
                    {option}
                  </span>
                </label>
              ))}
            </div>
            {formErrors.pregnant && (
              <p 
                className="text-red-600 text-base font-semibold mt-3 font-heading-sub" 
                role="alert"
              >
                ⚠️ {formErrors.pregnant}
              </p>
            )}
          </fieldset>

          {/* Message Field */}
          <div>
            <label 
              className="block font-semibold text-gray-800 mb-3 text-lg font-heading-sub" 
              htmlFor="message"
            >
              {t('contact.messageLabel')}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows="6"
              disabled={isSubmitting}
              placeholder={t('contact.messagePlaceholder', { familyTypeText: familyTypeText.toLowerCase() })}
              className={`w-full px-6 py-4 rounded-2xl border focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-lg resize-vertical transition-colors duration-200 font-body ${
                isSubmitting 
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            />
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn-primary text-lg px-12 py-4 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent ${
                isSubmitting 
                  ? 'opacity-50 cursor-not-allowed' 
                  : ''
              }`}
              aria-describedby="submit-status"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('financialModal.buttons.submitting').toUpperCase()}
                </span>
              ) : (
                t('financialModal.buttons.submit').toUpperCase()
              )}
            </button>
          </div>

          {/* reCAPTCHA Badge Note */}
          {recaptchaSiteKey && (
            <div className="text-center">
              <p className="text-sm text-gray-500 font-body">
                {t('financialModal.recaptchaNotice')}
              </p>
            </div>
          )}
        </form>
      </div>
    </section>
  )
}

export default ContactForm